import { type NostrEvent, type NostrMetadata, NSchema as n } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { loadProfile } from '@/lib/profileLoader';

/**
 * Profile metadata for a pubkey.
 *
 * Requests are batched by `loadProfile`: every profile needed within a short
 * window (e.g. all authors of a freshly rendered feed page) is fetched with a
 * single relay query instead of one query per card.
 *
 * Deliberately conservative settings:
 *  - A missing profile caches as an empty result instead of throwing, so we
 *    don't trigger retry storms for authors whose kind-0 simply isn't on our
 *    relays.
 *  - No `refetchOnWindowFocus`: refetching every visible profile each time
 *    the tab regains focus floods the relays (especially on mobile).
 */
export function useAuthor(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<{ event?: NostrEvent; metadata?: NostrMetadata }>({
    queryKey: ['nostr', 'author', pubkey ?? ''],
    queryFn: async ({ signal }) => {
      if (!pubkey) return {};

      const event = await loadProfile(nostr, pubkey, signal);
      if (!event) {
        // Query succeeded but no profile found — cache the miss.
        return {};
      }

      try {
        const metadata = n.json().pipe(n.metadata()).parse(event.content);
        return { metadata, event };
      } catch {
        return { event };
      }
    },
    enabled: !!pubkey,
    staleTime: 2 * 60 * 1000, // Re-fetch profiles after 2 minutes
    retry: 1, // one retry for transient relay failures, then give up quietly
    retryDelay: 1000,
  });
}
